import {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import {useTripChat} from '@/hooks/useTripChat'
import {useTripDetails} from '@/hooks/useTrips'
import {useUser} from '@/hooks/useUser'
import {useDelayCountdown} from '@/hooks/useDelayCountdown'
import {useReportTripDelay} from '@/hooks/useReportTripDelay'
import {canReportDelay, delayFeedItems, resolveActiveDelay} from '@/features/trip-delay/mapTripDelay'
import {hasArrivalGuide} from '@/features/fromAirportArrival/arrivalEligibility'
import {usePickupArrivalConfig} from '@/hooks/useAppConfig'
import {useArrivedAtPickupPoint} from '@/hooks/useArrivedAtPickupPoint'
import {useMyLocation} from '@/hooks/useMyLocation'
import {tripFlightRecord} from '@/features/fromAirportArrival/tripFlightRecord'
import {formatWalkSummary, resolveWalkPlan} from '@/features/navigation/airportWalk'
import {yyzStaticWalkTotals} from '@/features/navigation/data/yyzStaticWalkSteps'
import {flightLandedPassed} from '@/features/fromAirportArrival/useFlightArrivalTrigger'
import {MESSAGE_MAX_LENGTH} from '@/services/chatService'
import {onTripDelay} from '@/services/reverb'
import {formatDelayTime} from '@/features/trip-delay/formatDelayTime'
import {parseAppZoneDate} from '@/utils/dateUtils'
import DelayReasonModal from '@/customComponents/tripDelay/DelayReasonModal.jsx'
import {
    DelayEntryCard,
    DelayEntrySkeleton,
    DelayMinutesRow,
    DelaySystemBubble,
    DelayWarningBanner,
    MeetYourDriverCard,
} from '@/customComponents/tripDelay/DelayPieces.jsx'

/**
 * THE THREAD — messages between a passenger and their driver, and the delay flow woven
 * through them.
 *
 * ── ⚠️ IT IS MOUNTED ONLY WHILE OPEN, AND THAT IS LOAD-BEARING ────────────────────
 * Opening it fetches the conversation, and fetching marks the driver's messages read with
 * no way to opt out. So this component must not be rendered in the background to "have it
 * ready": doing so would clear the unread badge for someone who never looked. The trip
 * query below is gated on `open` for the same reason it is gated at all — nothing here
 * runs against a thread nobody is looking at.
 *
 * ── ⚠️ A DELAY IS NOT A MESSAGE, AND THE FEED IS NOT THE MESSAGE LIST ─────────────
 * "Stuck at immigration, give me 15 minutes" is a row on the TRIP, not a chat message. The
 * server stores no messages for it and broadcasts none. What the reader sees as a five-part
 * exchange is drawn by this screen from `trip.requested_delays`, and the driver's app draws
 * the same five from the same row, mirrored. So the feed is messages and delays MERGED and
 * sorted by time — not a list of messages with extras appended.
 *
 * That merge is why every item carries an `atMs`, and why the sentinel values below exist:
 * the live parts of the flow have no timestamp of their own and have to be pinned to the
 * bottom in a fixed order.
 *
 * ── ⚠️ NEWEST AT THE BOTTOM, WHICH IS THE OPPOSITE OF THE DATA ────────────────────
 * The endpoint returns newest first and pages backwards through history. A chat reads the
 * other way round, so the feed is sorted ascending for display and older pages join at the
 * top as the reader scrolls up.
 */

/**
 * WHERE THE LIVE PARTS SIT.
 *
 * ⚠️ THEY ARE PINNED, NOT TIMESTAMPED, because none of them has happened yet — a pending
 * reason is a question on screen and the nudge is a prompt, neither of which is an event
 * with a time. Sorting them by `Date.now()` would work until a message arrived a second
 * later and jumped below them.
 *
 * The ordering between the three is the order they are answered in: what you picked, then
 * how long, then the nudge that comes at the end of the window.
 */
const PIN_ENTRY_AFTER_HISTORY = Number.MAX_SAFE_INTEGER - 3
/**
 * ⚠️ "MEET YOUR DRIVER" IS LAST, ALWAYS. Every other row is placed by when it happened;
 * this one is placed by being the thing you do NEXT, so it sits under the whole
 * conversation however old or new that conversation is.
 */
const PIN_MEET_DRIVER = Number.MAX_SAFE_INTEGER
/**
 * A banner whose own exchange is not in the feed.
 *
 * ⚠️ IT SHARES A PIN WITH THE ENTRY CARD, WHICH IS SAFE BECAUSE THEY CANNOT BOTH EXIST:
 * the card is offered only when NO delay is active and this only when one is. It happens
 * when `active_requested_delay` names a record that is not in `requested_delays` — rare,
 * and the honest place for it is the bottom of the thread rather than a guessed timestamp.
 */
const PIN_ORPHAN_BANNER = Number.MAX_SAFE_INTEGER - 3
const PIN_PENDING = Number.MAX_SAFE_INTEGER - 2
const PIN_NUDGE = Number.MAX_SAFE_INTEGER - 1

/**
 * How close to the end of a delay the "need more time?" prompt appears.
 *
 * ⚠️ THE MOBILE CODE ADMITS THIS IS A GUESS — its own comment says the spec gives no
 * value. Five minutes is long enough that a passenger still at a baggage belt can act on
 * it and short enough that it is not asked the moment they have answered.
 */
const ALMOST_UP_SECONDS = 300

/**
 * HOW CLOSE TO THE BOTTOM STILL COUNTS AS "AT THE BOTTOM".
 *
 * ⚠️ NOT ZERO. A container scrolled to its end is routinely a pixel or two short of
 * `scrollHeight - clientHeight` — sub-pixel line heights, a scrollbar, a zoomed page — so an
 * exact test reports a reader who is plainly at the bottom as having scrolled up, and new
 * messages then quietly stop following.
 */
const BOTTOM_SLACK_PX = 48

/**
 * @param {Function} [onStartWalking] open the live walk. Absent on screens that have no
 *   walk to open, which is also what hides the "Meet Your Driver" card.
 */
export default function TripChat({tripId, driverName, driverPhoto, open, onClose, onStartWalking}) {
    const {messages, loading, sending, error, hasMore, loadMore, send, clearError} = useTripChat(tripId, {enabled: open})

    /* ⚠️ THE TRIP IS THE DELAY'S ONLY SOURCE. There is no delay endpoint to read — a
       reported delay comes back inside the trip detail and nowhere else — so the live poll
       here IS how the countdown, the banner and the entry card stay true. Gated on `open`
       so a closed thread costs nothing. */
    const {data: tripData} = useTripDetails(tripId, {live: true, enabled: !!tripId && open})
    const trip = tripData?.trip || null

    const {data: me} = useUser()
    const [draft, setDraft] = useState('')
    const scrollRef = useRef(null)

    /*
     * ── WHERE THE READER WAS ──────────────────────────────────────
     *
     * ⚠️ A REOPENED THREAD IS NOT A NEW ONE. Closing this sheet to look at the map and
     * coming back is one reading session, not two — and this thread is long, because every
     * delay is five bubbles. Dropping someone back at the bottom (or worse, wherever the
     * browser left the scrollbar as the list rebuilt) means finding their place again by
     * hand each time.
     *
     * ⚠️ REFS, NOT STATE, ALL THREE. A scroll position that re-rendered the component on
     * every wheel event would re-render a list this size sixty times a second, and none of
     * these values is ever drawn — they only ever feed a `scrollTop`.
     *
     * ⚠️ AND IT SURVIVES THE LIST BEING EMPTIED. Reopening refetches, and the fetch
     * clears `messages` before the response lands — so the container really does collapse
     * to nothing mid-flight. The offset is remembered here rather than read off the DOM,
     * which by then says zero.
     */
    const savedScrollRef = useRef(null)
    /* Whether the reader was at the bottom when they left. Distinct from the offset: on a
       thread that has GROWN since, "the bottom" is a different number and the saved one
       would strand them just above the new messages. */
    const wasAtBottomRef = useRef(true)
    /* Set the moment the sheet opens, cleared once the restore has actually happened — the
       feed arrives in stages (messages, then the trip's delays) and only the first render
       with content in it can be positioned. */
    const restorePendingRef = useRef(false)

    /* ⚠️ THE SOCKET IS A HINT, THE TRIP IS THE TRUTH. `trip.delay.requested` carries an
       undocumented payload nobody parses — what it is FOR is telling this screen that the
       2s poll is about to be wrong. Invalidating is a second's head start on a pickup time
       that has just moved, which is worth having and is all this can honestly do. */
    const queryClient = useQueryClient()
    useEffect(() => {
        if (!open || !tripId) return undefined
        return onTripDelay(tripId, () => {
            queryClient.invalidateQueries({queryKey: ['trip', tripId]})
        })
    }, [open, tripId, queryClient])

    /* ── The delay flow's own state ───────────────────────────────────────────────
       `pendingReason` is the gap between picking a reason and picking minutes. It is
       LOCAL and deliberately not persisted: it is not a delay until the minutes are sent,
       and a half-answered question restored on the next open would be a passenger asked
       to finish something they had walked away from. */
    const [reasonOpen, setReasonOpen] = useState(false)
    /* Bumped on every open so the picker REMOUNTS rather than remembering the reason
       somebody backed out of last time — see the note on `DelayReasonModal`. */
    const [reasonKey, setReasonKey] = useState(0)
    const [pendingReason, setPendingReason] = useState(null)
    const {report, submittingMinutes, error: delayError, clearError: clearDelayError} = useReportTripDelay(tripId)

    const activeDelay = resolveActiveDelay(trip)
    const countdown = useDelayCountdown(activeDelay)

    /* ⚠️ THE FLIGHT CHECK IS FREE HERE. Mobile answers this with a direct, billed
       AeroDataBox call from the device on every chat open; the web trip payload already
       carries the backend's own flight poll. See `flightLandedPassed`. */
    const landed = flightLandedPassed(trip)
    const showDelayEntry = !pendingReason && canReportDelay(trip, {flightLandedPassed: landed})
    /* The same slot, while the trip that would answer the question has not arrived. Only
       when nothing ELSE disqualifies the trip, so it never flashes on an ineligible ride. */
    const showDelaySkeleton = !showDelayEntry && !pendingReason && !trip && !!tripId

    /*
     * ── THE WALK THAT IS STILL AHEAD OF THEM ──────────────────────────────────────
     *
     * ⚠️ IT IS NOT PART OF THE DELAY FLOW, it only shares the feed. A passenger who opened
     * this thread to report a delay is one tap from the walk that follows; one who opened it
     * for any other reason has the same walk ahead of them.
     *
     * ⚠️ AND IT GOES AWAY ONCE THEY ARE THERE. Fifty metres from the booth the walk is over,
     * and a card offering to start it is telling someone standing at the kerb to set off.
     * That is the ONLY thing the arrival latch gates here — it has nothing to do with
     * whether a delay may be reported (see `canReportDelay`).
     */
    /* `here` rather than `me` — `me` on this screen is the signed-in ACCOUNT, and two
       different meanings for one word in one component is how a bug gets written. */
    const here = useMyLocation({enabled: open && hasArrivalGuide(trip)})
    const walkFlight = tripFlightRecord(trip)
    const walkPlan = useMemo(
        () => (hasArrivalGuide(trip) ? resolveWalkPlan(here.position, walkFlight) : null),
        [trip, here.position, walkFlight],
    )
    const {radiusM: pickupRadiusM} = usePickupArrivalConfig('passenger', 'from_airport')
    const reachedPickup = useArrivedAtPickupPoint(here.position, walkPlan?.pickup || null, pickupRadiusM)
    const walkSummary = useMemo(
        () => (walkPlan ? formatWalkSummary(yyzStaticWalkTotals(walkPlan.terminal, walkPlan.scope)) : ''),
        [walkPlan],
    )
    const showMeetDriver = !!walkPlan && !reachedPickup && !!trip?.driver && onStartWalking != null

    const myFace = <Face src={me?.profile_picture} name={me?.full_name || me?.display_name || 'You'}/>
    const driverFace = <Face src={driverPhoto} name={driverName || 'Driver'}/>

    /**
     * THE FEED — messages and delays in one ordered list.
     *
     * ⚠️ SORTED, NOT CONCATENATED. A delay reported an hour ago belongs an hour back in
     * the thread, not under the newest message, and messages sent during a delay belong
     * after it. Only a merge on time gets both right.
     */
    const feed = useMemo(() => {
        const items = []

        /* The entry card opens the thread when there is no delay history, and re-offers
           itself at the BOTTOM once there is — an expired delay's exchange stays where it
           happened, and the invitation to report another belongs after it. */
        if (showDelayEntry || showDelaySkeleton) {
            items.push({
                kind: showDelayEntry ? 'entry' : 'entrySkeleton',
                id: 'delay-entry',
                atMs: trip?.requested_delays?.length ? PIN_ENTRY_AFTER_HISTORY : 1,
            })
        }

        /* ⚠️ A MESSAGE WITH NO `created_at` STILL HAS TO LAND SOMEWHERE. Falling to 0
           would drag it to the very top, above history from days earlier. Ids are
           monotonic, so walking them in order and taking "just after the last one placed"
           keeps an untimed message beside the ones it was sent between. */
        /* ⚠️ AND THE ID ORDER WINS OVER THE CLOCK. Each message is placed no earlier than
           the one before it by id, so a `created_at` that reads out of order (a different
           format, a zone mix-up) can no longer flip the thread upside down — newest stays at
           the bottom. The timestamp still places delays between messages. */
        const delayItems = delayFeedItems(trip?.requested_delays)

        /* ⚠️ AN UNTIMED MESSAGE IS A NEW ONE. A message with no readable `created_at` is
           one just sent or just pushed over the socket — the loaded history always carries
           it. Placed at "just after the last message" it could still sit ABOVE a delay
           reported earlier (a thread whose only messages are that one landed at the very
           top), so it goes after the newest delay too. */
        const newestDelayMs = delayItems.reduce((max, item) => Math.max(max, item.atMs || 0), 0)
        let lastMessageMs = 0
        for (const message of [...messages].sort((a, b) => Number(a.id) - Number(b.id))) {
            const ms = parseAppZoneDate(message.createdAt ?? message.readAt)?.getTime()
            const atMs = Number.isFinite(ms)
                ? Math.max(ms, lastMessageMs + 1)
                : Math.max(lastMessageMs, newestDelayMs) + 1
            lastMessageMs = Math.max(lastMessageMs, atMs)
            items.push({kind: 'message', id: `msg-${message.id}`, atMs, message})
        }

        for (const item of delayItems) {
            items.push({kind: 'delay', id: item.id, atMs: item.atMs, delay: item.delay})
        }

        /* The banner belongs directly under the exchange it is counting down, so it takes
           that group's own time plus one rather than a pin of its own. */
        if (activeDelay) {
            const group = items.find((i) => i.kind === 'delay' && i.delay.id === activeDelay.id)
            items.push({
                kind: 'banner',
                id: `delay-banner-${activeDelay.id}`,
                atMs: group ? group.atMs + 1 : PIN_ORPHAN_BANNER,
                delay: activeDelay,
            })
            if (countdown.remainingSeconds <= ALMOST_UP_SECONDS) {
                items.push({kind: 'nudge', id: `nudge-${activeDelay.id}`, atMs: PIN_NUDGE, delay: activeDelay})
            }
        }

        if (pendingReason) {
            items.push({kind: 'pending', id: 'delay-pending', atMs: PIN_PENDING, reason: pendingReason})
        }

        if (showMeetDriver) {
            items.push({kind: 'meetDriver', id: 'meet-driver', atMs: PIN_MEET_DRIVER})
        }

        return items.sort((a, b) => a.atMs - b.atMs)
    }, [messages, trip, activeDelay, countdown.remainingSeconds, pendingReason, showDelayEntry, showDelaySkeleton, showMeetDriver])

    /* Remembered on every scroll, because there is no event for "the sheet is closing"
       that fires before the list is torn down. */
    const rememberScroll = () => {
        const el = scrollRef.current
        if (!el || restorePendingRef.current) return
        savedScrollRef.current = el.scrollTop
        wasAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_SLACK_PX
    }

    /* Arm the restore when the sheet opens. `open` is the trigger, not mount: this
       component stays mounted while closed. */
    useEffect(() => {
        if (open) restorePendingRef.current = true
    }, [open])

    /*
     * PUT THE SCROLLBAR WHERE IT BELONGS — on open, and as the thread grows.
     *
     * ⚠️ `useLayoutEffect`, SO THE READER NEVER SEES THE WRONG POSITION. A normal effect
     * paints the list at the top first and jumps a frame later, which on a thread this long
     * is a visible lurch every time the sheet opens.
     *
     * ⚠️ AND IT FOLLOWS THE BOTTOM ONLY IF THAT IS WHERE THE READER ALREADY WAS. A new
     * message must not yank someone who has scrolled up to re-read what their driver said.
     * The countdown re-renders this screen once a second, so this is keyed on the newest
     * item's id — an unchanged thread does not move.
     */
    const newestKey = feed[feed.length - 1]?.id
    const feedLength = feed.length
    useLayoutEffect(() => {
        const el = scrollRef.current
        if (!el || !open) return

        if (restorePendingRef.current) {
            /* Nothing to position against yet — the refetch has emptied the list and the
               response has not landed. Stay armed and try again on the next render. */
            if (!feedLength) return
            restorePendingRef.current = false

            const saved = savedScrollRef.current
            /* First open, or they left at the bottom: the bottom of a thread that may have
               grown since, not the offset that used to be the bottom. */
            el.scrollTop = (saved == null || wasAtBottomRef.current) ? el.scrollHeight : saved
            return
        }

        if (wasAtBottomRef.current) el.scrollTop = el.scrollHeight
    }, [open, newestKey, feedLength])

    const submit = async (e) => {
        e.preventDefault()
        const text = draft.trim()
        if (!text || sending) return

        /* ⚠️ THE COMPOSER IS CLEARED ONLY ON SUCCESS. Losing a typed message to a failed
           request is worse than the failure — the text is the part that cannot be
           recovered. */
        /* ⚠️ SENDING PUTS YOU BACK AT THE BOTTOM, wherever you were reading. Typing is a
           deliberate act at the end of the thread; leaving the view up in history after it
           looks like the message never sent. */
        if (await send(text)) {
            wasAtBottomRef.current = true
            setDraft('')
        }
    }

    /** Picking a chip IS the send — see `DelayMinutesChip`. Kept only on failure. */
    const submitMinutes = async (reason, minutes) => {
        const ok = await report({reasonCode: reason.id, reasonText: reason.label, minutes})
        if (ok) setPendingReason(null)
    }

    return (
        <>
            <Modal isOpen={open} onClose={onClose}>
                <ModalHeader title={driverName ? `Message ${driverName}` : 'Messages'} onClose={onClose}/>

                <div className="flex h-[60vh] flex-col">
                    <div ref={scrollRef} onScroll={rememberScroll} className="flex-1 overflow-y-auto pr-1">
                        {hasMore && (
                            <button
                                type="button"
                                onClick={loadMore}
                                className="mx-auto mb-3 block cursor-pointer rounded-full border border-(--border-color) bg-transparent px-4 py-1.5 text-xs font-semibold text-(--text-secondary)"
                            >
                                Load earlier messages
                            </button>
                        )}

                        {loading && !feed.length && (
                            <p className="py-8 text-center text-sm text-(--text-secondary)">Loading…</p>
                        )}

                        {!loading && !feed.length && (
                            <p className="py-8 text-center text-sm text-(--text-secondary)">
                                No messages yet. Anything you send here goes straight to your driver.
                            </p>
                        )}

                        <div className="flex flex-col gap-3">
                            {feed.map((item) => {
                                switch (item.kind) {
                                    case 'entry':
                                        return (
                                            <DelayEntryCard
                                                key={item.id}
                                                driverFace={driverFace}
                                                showQuestion={!trip?.requested_delays?.length}
                                                onOpen={() => { clearDelayError(); setReasonKey((n) => n + 1); setReasonOpen(true) }}
                                            />
                                        )
                                    case 'entrySkeleton':
                                        return <DelayEntrySkeleton key={item.id}/>
                                    case 'meetDriver':
                                        return (
                                            <MeetYourDriverCard
                                                key={item.id}
                                                walkSummary={walkSummary}
                                                pickupLabel={walkPlan?.endLabel}
                                                onStartWalking={onStartWalking}
                                            />
                                        )
                                    case 'delay':
                                        return (
                                            <DelaySystemBubble
                                                key={item.id}
                                                delay={item.delay}
                                                myFace={myFace}
                                                driverFace={driverFace}
                                            />
                                        )
                                    case 'banner':
                                        return (
                                            <DelayWarningBanner
                                                key={item.id}
                                                reasonText={item.delay.reason_text}
                                                label={countdown.label}
                                            />
                                        )
                                    case 'pending':
                                        return (
                                            <PendingReason
                                                key={item.id}
                                                reason={item.reason}
                                                myFace={myFace}
                                                driverFace={driverFace}
                                                submitting={submittingMinutes}
                                                error={delayError}
                                                onPick={(minutes) => submitMinutes(item.reason, minutes)}
                                            />
                                        )
                                    case 'nudge':
                                        return (
                                            <AlmostUp
                                                key={item.id}
                                                driverFace={driverFace}
                                                submitting={submittingMinutes}
                                                error={delayError}
                                                busy={sending}
                                                onExtend={(minutes) => report({
                                                    reasonCode: item.delay.reason_code,
                                                    reasonText: item.delay.reason_text,
                                                    minutes,
                                                })}
                                                onOnMyWay={() => send('I’m on my way')}
                                            />
                                        )
                                    default:
                                        return (
                                            <Bubble
                                                key={item.id}
                                                message={item.message}
                                                driverName={driverName}
                                                driverPhoto={driverPhoto}
                                                myPhoto={me?.profile_picture}
                                                myName={me?.full_name || me?.display_name || me?.first_name}
                                            />
                                        )
                                }
                            })}
                        </div>
                    </div>

                    {error && <p className="mb-0! pt-2 text-sm text-(--text-red)" role="alert">{error}</p>}

                    <form onSubmit={submit} className="flex items-end gap-2 pt-3">
                        <textarea
                            value={draft}
                            onChange={(e) => { setDraft(e.target.value); if (error) clearError() }}
                            /* Enter sends, shift-enter breaks a line — the convention every
                               chat uses, and getting it backwards is immediately annoying. */
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) submit(e) }}
                            rows={1}
                            maxLength={MESSAGE_MAX_LENGTH}
                            placeholder="Type a message"
                            className="max-h-28 min-h-[48px] flex-1 resize-none rounded-2xl border border-(--border-color) bg-(--bg-white) px-4 py-3 text-sm outline-none"
                        />
                        <button
                            type="submit"
                            disabled={!draft.trim() || sending}
                            className="h-12 shrink-0 cursor-pointer rounded-full border-0 bg-(--bg-dark) px-5 text-sm font-semibold text-white disabled:opacity-40 max-md:px-4"
                        >
                            {sending ? 'Sending…' : 'Send'}
                        </button>
                    </form>
                </div>
            </Modal>

            {/* ⚠️ A SIBLING OF THE CHAT, NOT A CHILD OF IT. Two dialogs, so the reason
                picker sits over the thread the way it does on mobile — and closing it
                returns to a thread that never unmounted, so the fetch it would have cost
                (and the read receipts that fetch would have set) never happens. */}
            <DelayReasonModal
                key={reasonKey}
                open={reasonOpen}
                onClose={() => setReasonOpen(false)}
                delays={trip?.requested_delays}
                onPicked={(reason) => { setReasonOpen(false); setPendingReason(reason) }}
            />
        </>
    )
}

/**
 * BETWEEN THE REASON AND THE MINUTES.
 *
 * ⚠️ THE DRIVER'S LINE HERE IS SYNTHETIC. No driver typed "How much more time do you think
 * you need?" — the product is asking in their voice, exactly as the mobile apps do, so the
 * exchange reads as a conversation rather than as a form. Every one of these is scripted
 * copy shared with the driver's app; see `DelayPieces`.
 *
 * ⚠️ AND THERE IS NO CANCEL. Mobile has none either: leaving the screen discards it, which
 * here means closing the sheet. A visible cancel would be a fourth thing to press beside
 * three chips that already answer the question.
 */
function PendingReason({reason, myFace, driverFace, submitting, error, onPick}) {
    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-end justify-end gap-2">
                <div className="max-w-[75%] rounded-2xl bg-(--bg-dark) px-3 py-2 text-sm text-white">
                    <p className="mb-0! whitespace-pre-wrap">{reason.label}</p>
                </div>
                {myFace}
            </div>
            <div className="flex items-end gap-2">
                {driverFace}
                <div className="max-w-[75%] rounded-2xl bg-(--bg-grey) px-3 py-2 text-sm text-(--text-primary)">
                    <p className="mb-0!">How much more time do you think you need?</p>
                </div>
            </div>
            <DelayMinutesRow onPick={onPick} submitting={submitting}/>
            {/* ⚠️ THE REASON SURVIVES A FAILURE. The chips re-enable and the picked reason
                stays on screen, so a retry is one tap and not the whole flow again. */}
            {error && <p className="mb-0! text-sm text-(--text-red)" role="alert">{error}</p>}
        </div>
    )
}

/**
 * THE LAST FIVE MINUTES OF A WINDOW.
 *
 * ⚠️ "I'm on my way" IS A REAL CHAT MESSAGE, the only chip in this flow that is. The other
 * two report a delay; this one tells the driver the delay is over, and there is no API for
 * ending a window early — so it says so in the thread, where the driver is already reading.
 *
 * ⚠️ EXTENDING MAY BE REJECTED. The server refuses a second delay while one is running
 * (`requested_delay_active`), and this prompt only exists WHILE one is running — so unless
 * the backend treats an extension as a special case, these two chips answer with that
 * error until the window closes. Unconfirmed with the backend team; the error is surfaced
 * rather than hidden precisely so it is visible if that is what happens.
 */
function AlmostUp({driverFace, submitting, error, busy, onExtend, onOnMyWay}) {
    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-end gap-2">
                {driverFace}
                <div className="max-w-[75%] rounded-2xl bg-(--bg-grey) px-3 py-2 text-sm text-(--text-primary)">
                    <p className="mb-0!">Your wait time is almost up. Do you need more time?</p>
                </div>
            </div>
            <div className="flex flex-wrap gap-2">
                {[15, 30].map((minutes) => (
                    <button
                        key={minutes}
                        type="button"
                        disabled={submitting != null || busy}
                        onClick={() => onExtend(minutes)}
                        className="cursor-pointer rounded-full border border-(--border-color) bg-(--bg-white) px-4 py-2 text-sm font-semibold text-(--text-primary) disabled:cursor-default disabled:opacity-40"
                    >
                        {submitting === minutes ? 'Sending…' : `Yes, ${minutes} more min`}
                    </button>
                ))}
                <button
                    type="button"
                    disabled={submitting != null || busy}
                    onClick={onOnMyWay}
                    className="cursor-pointer rounded-full border-0 bg-(--bg-dark) px-4 py-2 text-sm font-semibold text-white disabled:cursor-default disabled:opacity-40"
                >
                    I&apos;m on my way
                </button>
            </div>
            {error && <p className="mb-0! text-sm text-(--text-red)" role="alert">{error}</p>}
        </div>
    )
}

/** A face for a bubble: their picture, or their initial. */
function Face({src, name}) {
    if (src) return <img src={src} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover"/>
    return (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-(--bg-grey) text-xs font-semibold text-(--text-secondary)">
            {String(name || '?').charAt(0).toUpperCase()}
        </span>
    )
}

/**
 * ONE MESSAGE — avatar, bubble, and the time and receipt tucked into its bottom corner.
 *
 * ⚠️ MINE IS `sender_type === 'passenger'` AND EVERYTHING ELSE IS THEIRS. The type is an
 * open string on purpose, so a future 'support' or 'system' sender renders as someone
 * else's message rather than as one of the reader's own.
 *
 * ⚠️ THE TIME SITS INSIDE THE BUBBLE, not under it. A line of its own doubles the vertical
 * space every message takes and pushes the newest one off a short thread; in the corner it
 * costs nothing and is read only when someone looks for it.
 */
function Bubble({message, driverName, driverPhoto, myPhoto, myName}) {
    const mine = message.isMine
    const sender = message.sender

    /* ⚠️ THREE SOURCES, IN ORDER OF HOW MUCH THEY KNOW. The message's own sender is the
       most specific and the least reliable — it is nullable, and thinnest on your own
       messages. Behind it sits the person this thread is with, or the reader's own
       account, both of which are always present. The initial is the last resort. */
    const avatar = sender?.profile_picture || (mine ? myPhoto : driverPhoto)
    const displayName = sender?.display_name
        || sender?.full_name
        || (mine ? myName : driverName)
        || (mine ? 'You' : 'Driver')

    /* Both sides get a face, each on its own edge: theirs on the left, the reader's on the
       right, so a glance down the thread reads as a conversation rather than a list. */
    const face = <Face src={avatar} name={displayName}/>

    return (
        <div className={`flex items-end gap-2 ${mine ? 'justify-end' : 'justify-start'}`}>
            {!mine && face}

            <div
                className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                    mine ? 'bg-(--bg-dark) text-white' : 'bg-(--bg-grey) text-(--text-primary)'
                }`}
            >
                <p className="mb-0! whitespace-pre-wrap">{message.message}</p>

                <span className={`mt-1 flex items-center justify-end gap-1 text-[11px] ${
                    mine ? 'text-white/60' : 'text-(--text-secondary)'
                }`}>
                    {/* ⚠️ A NULL TIMESTAMP RENDERS NOTHING. `created_at` is nullable, and a
                        confident "12:00" invented from nothing is worse than no time.
                        Parsed in the app zone: the server sends Toronto wall clock with no
                        offset on it, which a bare `new Date` reads as the viewer's own. */}
                    {formatDelayTime(message.createdAt)}
                    {/* ⚠️ RECEIPTS ONLY ON YOUR OWN MESSAGES. A tick on a message someone
                        sent YOU tells them something they already know. */}
                    {mine && <Ticks read={message.isRead}/>}
                </span>
            </div>

            {mine && face}
        </div>
    )
}

/**
 * SENT, OR READ.
 *
 * ⚠️ TWO STATES, NOT THREE. The familiar pattern has a third — sent, delivered, read — and
 * this API reports no delivery: a message carries `is_read` and nothing else. So one tick
 * means it left here and two mean it was read, with no grey double-tick pretending to know
 * about delivery.
 */
function Ticks({read}) {
    return (
        <svg
            viewBox="0 0 20 12"
            className={`h-3 w-4 shrink-0 ${read ? 'text-(--accent-green)' : 'text-current'}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-label={read ? 'Read' : 'Sent'}
        >
            <path d="M1 6.5 4.5 10 11 2"/>
            {read && <path d="M8 6.5 11.5 10 18 2"/>}
        </svg>
    )
}

export {TripChat}
